// apps/admin/src/features/audit/action-label.ts
//
// Shared audit `action` label resolver.
//
// The Recent + Archive audit tables (and the detail drawer heading) used to
// render the raw backend action code (e.g. `AUTH_USER_UPDATE`,
// `blueprint:create`, `products_special.create`) directly, so every row showed
// an untranslated technical string regardless of the active locale.
//
// The action vocabulary is open-ended and mixes conventions (UPPER_SNAKE,
// `ns:verb`, `dotted.path`). i18next reserves `:` (namespace) and `.` (key)
// as separators, so only clean UPPER_SNAKE codes are safe as
// `audit.actionLabels.*` keys — anything containing `.`/`:` can never resolve
// as a nested key and is humanized instead. Mapped codes get a proper
// EN/VI/FR label; everything else degrades to a readable humanized form
// ("Blueprint create") rather than a raw code.

type TFn = (key: string, vars?: Record<string, unknown>) => string;

/** Turn a raw action code into a readable, capitalized phrase. */
export function humanizeAction(value: string): string {
  if (!value) return '—';
  const words = value
    .split(/[:._\s/-]+/)
    .filter(Boolean)
    .map((w) => w.toLowerCase());
  const first = words[0];
  if (!first) return value;
  words[0] = first.charAt(0).toUpperCase() + first.slice(1);
  return words.join(' ');
}

/**
 * Resolve an audit action code to a localized label. Falls back to a humanized
 * phrase when the code is not in the `audit.actionLabels` catalog or contains
 * i18next-reserved separators.
 */
export function actionLabel(t: TFn, value: string): string {
  if (!value) return '—';
  if (/[.:]/.test(value)) return humanizeAction(value);
  return t(`audit.actionLabels.${value}`, { defaultValue: humanizeAction(value) });
}
